import { useCallback, useMemo } from "react";
import QRCode from "qrcode.react";
import { Agent } from '@/types/agent/agent';
import { QRDisplayConfig, QRDownloadFormat, QRLabelOption } from '@/types/ui/branding';
import { QRCodeStorageService } from '@/services/qr-code/QRCodeStorageService';

interface QRGeneratorOptions {
  size?: number;
  includeMargin?: boolean;
}

const getLabelText = (label: QRLabelOption, genieName: string): string | null => {
  switch (label) {
    case 'scan-to-connect':
      return 'Scan to Connect';
    case 'scan-to-chat':
      return 'Scan to Chat';
    case 'talk-to-genie':
      return `Talk to ${genieName}`;
    case 'none':
      return null;
  }
};

export const useQRGenerator = (value: string, genie: Agent, options: QRGeneratorOptions = {}) => {
  const { size = 400, includeMargin = false } = options;

  const brandingConfig = useMemo(() => {
    const hasCustomBranding = !!(genie.branding?.logoUrl || (genie.branding?.primaryColor && genie.branding.primaryColor !== "#4E9CFF"));

    const primaryColor = hasCustomBranding ? genie.branding?.primaryColor || "#4E9CFF" : "#4E9CFF";
    const secondaryColor = hasCustomBranding ? genie.branding?.secondaryColor || primaryColor : "#3B82F6";

    return {
      hasCustomBranding,
      primaryColor,
      secondaryColor,
      backgroundColor: genie.branding?.qrCodeStyle?.backgroundColor || "#FFFFFF",
      foregroundColor: genie.branding?.qrCodeStyle?.foregroundColor || "#000000",
      logoUrl: hasCustomBranding ? genie.branding?.logoUrl : null,
      gradientEnabled: genie.branding?.gradientEnabled ?? !hasCustomBranding,
      gradientAngle: genie.branding?.gradientAngle || 135,
    };
  }, [genie.branding]);

  const generateQRCanvas = useCallback(async (displayConfig?: QRDisplayConfig): Promise<HTMLCanvasElement> => {
    const config = displayConfig || {
      style: 'framed',
      label: 'scan-to-connect',
      transparentBackground: false,
      showLogo: true,
      foregroundColor: '#000000',
      backgroundColor: '#FFFFFF'
    };
    const labelText = getLabelText(config.label, genie.name);

    // Use colors from displayConfig
    const qrForegroundColor = config.foregroundColor || brandingConfig.foregroundColor;
    const qrBackgroundColor = config.backgroundColor || brandingConfig.backgroundColor;

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d")!;

    // Calculate dimensions based on style
    let frameSize: number;
    let labelHeight = labelText ? 50 : 0;

    if (config.style === 'minimal') {
      frameSize = size + 32; // Just padding
    } else if (config.style === 'card') {
      frameSize = size + 48;
      labelHeight = labelText ? 60 : 40; // Extra space for genie name
    } else if (config.style === 'clean') {
      frameSize = size + 48;
    } else {
      // Framed style
      frameSize = size + 120;
    }

    canvas.width = frameSize;
    canvas.height = frameSize + (config.style === 'framed' ? 0 : labelHeight);
    canvas.style.width = `${frameSize}px`;
    canvas.style.height = `${frameSize + (config.style === 'framed' ? 0 : labelHeight)}px`;

    const bgColor = config.transparentBackground ? 'transparent' : qrBackgroundColor;

    // Draw background based on style
    if (config.style === 'framed') {
      if (config.transparentBackground) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      } else if (brandingConfig.gradientEnabled) {
        const gradient = ctx.createLinearGradient(0, 0, frameSize, frameSize);
        gradient.addColorStop(0, brandingConfig.primaryColor);
        gradient.addColorStop(1, brandingConfig.secondaryColor);
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.roundRect(0, 0, frameSize, frameSize, 20);
        ctx.fill();
      } else {
        ctx.fillStyle = brandingConfig.primaryColor;
        ctx.beginPath();
        ctx.roundRect(0, 0, frameSize, frameSize, 20);
        ctx.fill();
      }
    } else if (config.style === 'card' && !config.transparentBackground) {
      ctx.fillStyle = bgColor;
      ctx.beginPath();
      ctx.roundRect(0, 0, canvas.width, canvas.height, 16);
      ctx.fill();
      // Add border
      ctx.strokeStyle = '#e5e7eb';
      ctx.lineWidth = 1;
      ctx.stroke();
    } else if (config.style === 'clean' && !config.transparentBackground) {
      ctx.fillStyle = bgColor;
      ctx.beginPath();
      ctx.roundRect(0, 0, canvas.width, canvas.height, 16);
      ctx.fill();
      // Add colored border
      ctx.strokeStyle = brandingConfig.primaryColor + '33';
      ctx.lineWidth = 2;
      ctx.stroke();
    } else if (!config.transparentBackground) {
      ctx.fillStyle = bgColor;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }

    // Generate QR code
    const tempDiv = document.createElement("div");
    tempDiv.style.position = "absolute";
    tempDiv.style.left = "-10000px";
    tempDiv.style.top = "-10000px";
    tempDiv.style.width = `${size}px`;
    tempDiv.style.height = `${size}px`;
    tempDiv.style.overflow = "hidden";
    document.body.appendChild(tempDiv);

    const { createRoot } = await import("react-dom/client");
    const root = createRoot(tempDiv);

    const QRElement = QRCode({
      value,
      size,
      fgColor: qrForegroundColor,
      bgColor: config.transparentBackground ? 'transparent' : qrBackgroundColor,
      level: "H",
      includeMargin: includeMargin,
    });

    root.render(QRElement);
    await new Promise((resolve) => setTimeout(resolve, 200));

    const qrCanvas = tempDiv.querySelector("canvas");
    if (!qrCanvas) {
      root.unmount();
      document.body.removeChild(tempDiv);
      throw new Error("QR code canvas not found");
    }

    qrCanvas.style.width = `${size}px`;
    qrCanvas.style.height = `${size}px`;

    // Position QR code based on style
    let qrX: number;
    let qrY: number;

    if (config.style === 'framed') {
      // Framed style with QR container
      const qrPadding = 20;
      const qrContainerSize = size + qrPadding * 2;
      qrX = (frameSize - qrContainerSize) / 2;
      qrY = (frameSize - qrContainerSize - (labelText ? 60 : 0)) / 2;

      // Draw QR background container
      ctx.fillStyle = qrBackgroundColor;
      ctx.beginPath();
      ctx.roundRect(qrX, qrY, qrContainerSize, qrContainerSize, 16);
      ctx.fill();

      // Draw decorative corners
      ctx.strokeStyle = brandingConfig.primaryColor;
      ctx.lineWidth = 3;

      // Top-left corner
      ctx.beginPath();
      ctx.moveTo(qrX + 8, qrY + 20);
      ctx.lineTo(qrX + 8, qrY + 8);
      ctx.lineTo(qrX + 20, qrY + 8);
      ctx.stroke();

      // Top-right corner
      ctx.beginPath();
      ctx.moveTo(qrX + qrContainerSize - 20, qrY + 8);
      ctx.lineTo(qrX + qrContainerSize - 8, qrY + 8);
      ctx.lineTo(qrX + qrContainerSize - 8, qrY + 20);
      ctx.stroke();

      // Bottom-left corner
      ctx.beginPath();
      ctx.moveTo(qrX + 8, qrY + qrContainerSize - 20);
      ctx.lineTo(qrX + 8, qrY + qrContainerSize - 8);
      ctx.lineTo(qrX + 20, qrY + qrContainerSize - 8);
      ctx.stroke();

      // Bottom-right corner
      ctx.beginPath();
      ctx.moveTo(qrX + qrContainerSize - 20, qrY + qrContainerSize - 8);
      ctx.lineTo(qrX + qrContainerSize - 8, qrY + qrContainerSize - 8);
      ctx.lineTo(qrX + qrContainerSize - 8, qrY + qrContainerSize - 20);
      ctx.stroke();

      // Draw QR code
      ctx.drawImage(qrCanvas, qrX + qrPadding, qrY + qrPadding, size, size);

      // Draw label
      if (labelText) {
        const textY = qrY + qrContainerSize + 40;
        ctx.font = "500 14px Poppins, system-ui, sans-serif";
        const pillWidth = ctx.measureText(labelText).width + 40;
        ctx.fillStyle = "rgba(255, 255, 255, 0.95)";
        ctx.beginPath();
        ctx.roundRect((frameSize - pillWidth) / 2, textY - 15, pillWidth, 30, 15);
        ctx.fill();

        ctx.fillStyle = brandingConfig.primaryColor;
        ctx.textAlign = "center";
        ctx.fillText(labelText, frameSize / 2, textY + 5);
      }
    } else {
      // Other styles - simpler layout
      const padding = config.style === 'minimal' ? 16 : 24;
      qrX = (frameSize - size) / 2;
      qrY = padding;

      ctx.drawImage(qrCanvas, qrX, qrY, size, size);

      // Draw label/card text
      if (config.style === 'card') {
        const textY = qrY + size + 24;
        ctx.fillStyle = "#1f2937";
        ctx.font = "600 16px Poppins, system-ui, sans-serif";
        ctx.textAlign = "center";
        ctx.fillText(genie.name, frameSize / 2, textY);

        if (labelText) {
          ctx.fillStyle = brandingConfig.primaryColor;
          ctx.font = "400 13px Poppins, system-ui, sans-serif";
          ctx.fillText(labelText, frameSize / 2, textY + 20);
        }
      } else if (labelText) {
        const textY = qrY + size + 24;
        ctx.fillStyle = brandingConfig.primaryColor;
        ctx.font = "500 14px Poppins, system-ui, sans-serif";
        ctx.textAlign = "center";
        ctx.fillText(labelText, frameSize / 2, textY);
      }
    }

    // Clean up
    root.unmount();
    document.body.removeChild(tempDiv);

    // Add logo if enabled and URL is provided
    const showLogo = config.showLogo ?? true;
    if (showLogo && brandingConfig.logoUrl) {
      try {
        const logoImg = new Image();
        logoImg.crossOrigin = "anonymous";

        await new Promise<void>((resolve) => {
          logoImg.onload = () => {
            const logoSize = size * 0.15;
            let centerX: number;
            let centerY: number;

            if (config.style === 'framed') {
              const qrPadding = 20;
              const qrContainerSize = size + qrPadding * 2;
              const qrXPos = (frameSize - qrContainerSize) / 2;
              const qrYPos = (frameSize - qrContainerSize - (labelText ? 60 : 0)) / 2;
              centerX = qrXPos + qrPadding + size / 2;
              centerY = qrYPos + qrPadding + size / 2;
            } else {
              const padding = config.style === 'minimal' ? 16 : 24;
              centerX = frameSize / 2;
              centerY = padding + size / 2;
            }

            const logoX = centerX - logoSize / 2;
            const logoY = centerY - logoSize / 2;

            // Draw white background for logo
            ctx.fillStyle = config.transparentBackground ? '#FFFFFF' : qrBackgroundColor;
            ctx.fillRect(logoX - 5, logoY - 5, logoSize + 10, logoSize + 10);

            ctx.drawImage(logoImg, logoX, logoY, logoSize, logoSize);
            resolve();
          };
          logoImg.onerror = () => resolve();
          logoImg.src = brandingConfig.logoUrl!;
          setTimeout(() => resolve(), 2000);
        });
      } catch (error) {
        console.warn("Logo loading failed:", error);
      }
    }

    return canvas;
  }, [value, brandingConfig, size, includeMargin, genie.name]);

  const generateSVG = useCallback(async (displayConfig?: QRDisplayConfig): Promise<string> => {
    const config = displayConfig || {
      style: 'framed',
      label: 'scan-to-connect',
      transparentBackground: false,
      showLogo: true,
      foregroundColor: '#000000',
      backgroundColor: '#FFFFFF'
    };
    const labelText = getLabelText(config.label, genie.name);

    // Use colors from displayConfig
    const qrForegroundColor = config.foregroundColor || brandingConfig.foregroundColor;
    const qrBackgroundColor = config.backgroundColor || brandingConfig.backgroundColor;

    // Generate QR code as SVG using temporary element
    const tempDiv = document.createElement("div");
    tempDiv.style.position = "absolute";
    tempDiv.style.left = "-10000px";
    document.body.appendChild(tempDiv);

    const { createRoot } = await import("react-dom/client");
    const root = createRoot(tempDiv);

    const QRElement = QRCode({
      value,
      size,
      fgColor: qrForegroundColor,
      bgColor: config.transparentBackground ? 'transparent' : qrBackgroundColor,
      level: "H",
      includeMargin: false,
      renderAs: "svg",
    });

    root.render(QRElement);
    await new Promise((resolve) => setTimeout(resolve, 200));

    const qrSvg = tempDiv.querySelector("svg");
    const qrSvgContent = qrSvg ? qrSvg.innerHTML : '';

    root.unmount();
    document.body.removeChild(tempDiv);

    // Build SVG based on style
    let svgWidth: number;
    let svgHeight: number;
    let content = '';

    const bgColor = config.transparentBackground ? 'none' : qrBackgroundColor;

    if (config.style === 'framed') {
      svgWidth = size + 120;
      svgHeight = size + 120;
      const qrContainerSize = size + 40;
      const qrX = (svgWidth - qrContainerSize) / 2;
      const qrY = (svgHeight - qrContainerSize - (labelText ? 50 : 0)) / 2;

      // Background
      if (!config.transparentBackground) {
        if (brandingConfig.gradientEnabled) {
          content += `<defs><linearGradient id="grad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style="stop-color:${brandingConfig.primaryColor}"/>
            <stop offset="100%" style="stop-color:${brandingConfig.secondaryColor}"/>
          </linearGradient></defs>`;
          content += `<rect width="${svgWidth}" height="${svgHeight}" rx="20" fill="url(#grad)"/>`;
        } else {
          content += `<rect width="${svgWidth}" height="${svgHeight}" rx="20" fill="${brandingConfig.primaryColor}"/>`;
        }
      }

      // QR container
      content += `<rect x="${qrX}" y="${qrY}" width="${qrContainerSize}" height="${qrContainerSize}" rx="16" fill="${qrBackgroundColor}"/>`;

      // Decorative corners
      const cornerOffset = 8;
      content += `<path d="M${qrX + cornerOffset},${qrY + 20} L${qrX + cornerOffset},${qrY + cornerOffset} L${qrX + 20},${qrY + cornerOffset}" stroke="${brandingConfig.primaryColor}" stroke-width="3" fill="none"/>`;
      content += `<path d="M${qrX + qrContainerSize - 20},${qrY + cornerOffset} L${qrX + qrContainerSize - cornerOffset},${qrY + cornerOffset} L${qrX + qrContainerSize - cornerOffset},${qrY + 20}" stroke="${brandingConfig.primaryColor}" stroke-width="3" fill="none"/>`;
      content += `<path d="M${qrX + cornerOffset},${qrY + qrContainerSize - 20} L${qrX + cornerOffset},${qrY + qrContainerSize - cornerOffset} L${qrX + 20},${qrY + qrContainerSize - cornerOffset}" stroke="${brandingConfig.primaryColor}" stroke-width="3" fill="none"/>`;
      content += `<path d="M${qrX + qrContainerSize - 20},${qrY + qrContainerSize - cornerOffset} L${qrX + qrContainerSize - cornerOffset},${qrY + qrContainerSize - cornerOffset} L${qrX + qrContainerSize - cornerOffset},${qrY + qrContainerSize - 20}" stroke="${brandingConfig.primaryColor}" stroke-width="3" fill="none"/>`;

      // QR code
      content += `<g transform="translate(${qrX + 20}, ${qrY + 20})">${qrSvgContent}</g>`;

      // Label — pill width based on character count (approx 8px per char at 14px font + 40px padding)
      if (labelText) {
        const labelY = qrY + qrContainerSize + 35;
        const pillWidth = Math.max(80, labelText.length * 8 + 40);
        content += `<rect x="${(svgWidth - pillWidth) / 2}" y="${labelY - 15}" width="${pillWidth}" height="30" rx="15" fill="rgba(255,255,255,0.95)"/>`;
        content += `<text x="${svgWidth / 2}" y="${labelY + 5}" text-anchor="middle" font-family="Poppins, system-ui, sans-serif" font-size="14" font-weight="500" fill="${brandingConfig.primaryColor}">${labelText}</text>`;
      }
    } else {
      // Simpler styles
      const padding = config.style === 'minimal' ? 16 : 24;
      svgWidth = size + (padding * 2);
      svgHeight = size + (padding * 2) + (labelText ? (config.style === 'card' ? 50 : 30) : 0);

      // Background
      if (!config.transparentBackground) {
        if (config.style === 'card') {
          content += `<rect width="${svgWidth}" height="${svgHeight}" rx="16" fill="${bgColor}" stroke="#e5e7eb"/>`;
        } else if (config.style === 'clean') {
          content += `<rect width="${svgWidth}" height="${svgHeight}" rx="16" fill="${bgColor}" stroke="${brandingConfig.primaryColor}33" stroke-width="2"/>`;
        } else {
          content += `<rect width="${svgWidth}" height="${svgHeight}" rx="12" fill="${bgColor}"/>`;
        }
      }

      // QR code
      content += `<g transform="translate(${padding}, ${padding})">${qrSvgContent}</g>`;

      // Label
      if (config.style === 'card') {
        const textY = padding + size + 24;
        content += `<text x="${svgWidth / 2}" y="${textY}" text-anchor="middle" font-family="Poppins, system-ui, sans-serif" font-size="16" font-weight="600" fill="#1f2937">${genie.name}</text>`;
        if (labelText) {
          content += `<text x="${svgWidth / 2}" y="${textY + 20}" text-anchor="middle" font-family="Poppins, system-ui, sans-serif" font-size="13" fill="${brandingConfig.primaryColor}">${labelText}</text>`;
        }
      } else if (labelText) {
        const textY = padding + size + 20;
        content += `<text x="${svgWidth / 2}" y="${textY}" text-anchor="middle" font-family="Poppins, system-ui, sans-serif" font-size="14" font-weight="500" fill="${brandingConfig.primaryColor}">${labelText}</text>`;
      }
    }

    return `<svg xmlns="http://www.w3.org/2000/svg" width="${svgWidth}" height="${svgHeight}" viewBox="0 0 ${svgWidth} ${svgHeight}">${content}</svg>`;
  }, [value, brandingConfig, size, genie.name]);

  const downloadQR = useCallback(async (format: QRDownloadFormat = 'png', displayConfig?: QRDisplayConfig) => {
    try {
      const config: QRDisplayConfig = displayConfig || {
        style: 'framed',
        label: 'scan-to-connect',
        transparentBackground: false,
        showLogo: true,
        foregroundColor: '#000000',
        backgroundColor: '#FFFFFF',
      };
      const styleSuffix = config.style !== 'framed' ? `-${config.style}` : '';
      const filename = `${genie?.name || "genie"}-qr-code${styleSuffix}.${format}`;

      let blob: Blob;

      if (format === 'svg') {
        const svgContent = await generateSVG(config);
        blob = new Blob([svgContent], { type: 'image/svg+xml' });
        const url = URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.download = filename;
        link.href = url;
        link.click();

        URL.revokeObjectURL(url);
      } else {
        const canvas = await generateQRCanvas(config);
        const dataUrl = canvas.toDataURL("image/png");

        const link = document.createElement("a");
        link.download = filename;
        link.href = dataUrl;
        link.click();

        // Convert data URL to blob for storage
        const response = await fetch(dataUrl);
        blob = await response.blob();
      }

      // Persist to storage (fire-and-forget — don't block the download)
      if (genie?.id) {
        QRCodeStorageService.saveQRCode({
          agent_id: genie.id,
          format,
          display_config: config,
          target_url: value,
          blob,
          filename,
        }).catch((err) => {
          console.error('Failed to save QR to storage:', err);
        });
      }
    } catch (error) {
      console.error("Error downloading QR code:", error);
      throw new Error("Failed to download QR code");
    }
  }, [generateQRCanvas, generateSVG, genie?.name, genie?.id, value]);

  return {
    brandingConfig,
    generateQRCanvas,
    generateSVG,
    downloadQR,
  };
};
